/**
 * LAMED Frontend Entry Point
 *
 * TypeScript-based React application entry point.
 * Initializes the React application and mounts it to the DOM.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { setupGlobalErrorHandlers } from './services/errorReporting';
import './styles/index.css';

// Setup global error handlers for audit logging
setupGlobalErrorHandlers();

// Register Service Worker for PWA with auto-update
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('[PWA] Service Worker registered:', registration.scope);

        // Check for updates immediately on startup
        registration.update().then(() => {
          console.log('[PWA] Checked for updates on startup');
        });

        // Check for updates every 5 minutes (more frequent than hourly)
        setInterval(() => {
          registration.update();
          console.log('[PWA] Checking for updates...');
        }, 5 * 60 * 1000);

        // Listen for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available
                console.log('[PWA] New version available! Prompting user to update...');
                showUpdateNotification(newWorker);
              }
            });
          }
        });
      })
      .catch((error) => {
        console.error('[PWA] Service Worker registration failed:', error);
      });

    // Listen for controller changes (new service worker activated)
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        console.log('[PWA] New service worker activated - reloading page');
        refreshing = true;
        window.location.reload();
      }
    });
  });
} else {
  console.warn('[PWA] Service Workers are not supported in this browser');
}

// Show update notification with better UI and auto-update
function showUpdateNotification(newWorker: ServiceWorker) {
  // Create update notification banner
  const banner = document.createElement('div');
  banner.id = 'pwa-update-banner';
  banner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 16px;
    text-align: center;
    z-index: 999999;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    animation: slideDown 0.3s ease-out;
  `;

  // Auto-update countdown (force update after 10 seconds)
  let countdown = 10;
  const countdownElement = document.createElement('span');
  countdownElement.id = 'pwa-countdown';

  banner.innerHTML = `
    <div style="max-width: 600px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;">
      <div style="flex: 1; min-width: 200px;">
        <strong style="font-size: 16px; display: block; margin-bottom: 4px;">🎉 Update Available!</strong>
        <span style="font-size: 14px; opacity: 0.95;">A new version of LAMED is ready. Auto-updating in <span id="pwa-countdown">10</span>s...</span>
      </div>
      <div style="display: flex; gap: 8px;">
        <button id="pwa-update-btn" style="
          background: white;
          color: #667eea;
          border: none;
          padding: 10px 24px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          font-size: 14px;
          transition: transform 0.2s;
        ">Update Now</button>
      </div>
    </div>
  `;

  // Add animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideDown {
      from {
        transform: translateY(-100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
    #pwa-update-btn:hover {
      transform: scale(1.05);
    }
  `;
  document.head.appendChild(style);

  document.body.appendChild(banner);

  // Countdown timer for auto-update
  const countdownInterval = setInterval(() => {
    countdown--;
    const countdownEl = document.getElementById('pwa-countdown');
    if (countdownEl) {
      countdownEl.textContent = countdown.toString();
    }

    if (countdown <= 0) {
      clearInterval(countdownInterval);
      performUpdate(newWorker, banner);
    }
  }, 1000);

  // Update button click (immediate update)
  document.getElementById('pwa-update-btn')?.addEventListener('click', () => {
    clearInterval(countdownInterval);
    performUpdate(newWorker, banner);
  });
}

// Perform the update
function performUpdate(newWorker: ServiceWorker, banner: HTMLElement) {
  banner.innerHTML = `
    <div style="max-width: 600px; margin: 0 auto; text-align: center;">
      <strong style="font-size: 16px; display: block; margin-bottom: 8px;">⏳ Updating...</strong>
      <span style="font-size: 14px; opacity: 0.95;">Please wait while we update LAMED to the latest version.</span>
    </div>
  `;

  newWorker.postMessage({ type: 'SKIP_WAITING' });
  // The controllerchange event will reload the page
}

// Get the root element from the DOM
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Failed to find the root element. Check your index.html file.');
}

// Create React root and render the application
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
